import { createSelector } from '@reduxjs/toolkit';
import { denormalize } from 'normalizr';
import { selectGadgetEntities } from 'redux/gadget-update/gadget-update.adapter';
import { initialLayoutValue } from 'redux/initial-state';
import { RootState } from 'redux/store';
import { LayoutEditResponse } from 'types/entity';
import { layoutEditToLayout } from 'utils/converter/layout';
import {
  layoutSchema, selectLayoutById, selectLayoutEntities, selectLayoutIds,
} from './layout-update.adapter';
import { LayoutUpdateState } from './layout-update.slice';

const selectSelf = (state: RootState) => state;
const getLayoutState = createSelector(selectSelf, (state) => state.layoutUpdate);

const getLoaded = createSelector(
  getLayoutState,
  (state: LayoutUpdateState) => state.loaded,
);

const getProcessing = createSelector(
  getLayoutState,
  (state: LayoutUpdateState) => state.processing,
);

const getTouched = createSelector(
  getLayoutState,
  (state: LayoutUpdateState) => state.touched,
);

const getEntities = createSelector(
  selectSelf,
  (state: RootState) => {
    const layouts = selectLayoutEntities(state);
    const layoutIds = selectLayoutIds(state);
    const gadget = selectGadgetEntities(state);
    const denormalized: LayoutEditResponse[] = denormalize(
      { layouts: layoutIds },
      { layouts: [layoutSchema] },
      { layouts, gadget },
    ).layouts;
    return denormalized;
  },
);

const getEntitiesByLocale = createSelector(
  selectSelf,
  (state: RootState) => {
    const layouts = selectLayoutEntities(state);
    const layoutIds = selectLayoutIds(state);
    const gadget = selectGadgetEntities(state);
    const { locale } = state.userRead.entity;
    const denormalized: LayoutEditResponse[] = denormalize(
      { layouts: layoutIds },
      { layouts: [layoutSchema] },
      { layouts, gadget },
    ).layouts;
    return denormalized.map((layout) => layoutEditToLayout(layout, locale));
  },
);

const getSelectedEntity = createSelector(
  selectSelf,
  (state: RootState) => {
    const layout = selectLayoutById(state, state.layoutUpdate.selectedId);
    const layouts = { [layout?.layout_id || '']: layout };
    const gadget = selectGadgetEntities(state);
    return denormalize(
      { layouts: layout?.layout_id },
      { layouts: layoutSchema },
      { layouts, gadget },
    ).layouts as LayoutEditResponse;
  },
);

const getSelectedEntityByLocale = createSelector(
  selectSelf,
  (state: RootState) => {
    const layout = selectLayoutById(state, state.layoutUpdate.selectedId);
    const layouts = { [layout?.layout_id || '']: layout };
    const gadget = selectGadgetEntities(state);
    const { locale } = state.userRead.entity;
    const denormalized: LayoutEditResponse = denormalize(
      { layouts: layout?.layout_id },
      { layouts: layoutSchema },
      { layouts, gadget },
    ).layouts;
    return denormalized ? layoutEditToLayout(denormalized, locale) : initialLayoutValue;
  },
);

export const layoutUpdateQuery = {
  getLoaded,
  getProcessing,
  getTouched,
  getEntities,
  getEntitiesByLocale,
  getSelectedEntity,
  getSelectedEntityByLocale,
};
